/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, ChangeDetectionStrategy, Self, Optional, ChangeDetectorRef } from '@angular/core';
import { NgControl } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { WORKING_DAYS_DURATION_FIXED, WORKING_DAYS_END_DATE_IS_FIXED } from '@touchplan/strings';
import { distinctUntilChanged, filter, pluck, takeUntil, map, startWith, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, Subject, BehaviorSubject, timer } from 'rxjs';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { PlanState } from 'src/app/services/plan-state.service';
import { TicketEditService } from '../../ticket-edit.service';
import { Side } from '@touchplan/common';
import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { TicketType } from '@touchplan/common';

@Component({
	selector: 'ticket-work-days-message',
	templateUrl: './ticket-work-days-message.component.html',
	styleUrls: ['./ticket-work-days-message.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger('fadeInOut', [
			state('void', style({ opacity: 0 })),
			state('*', style({ opacity: 1 })),
			transition(':enter', animate('500ms ease-out')),
			transition(':leave', animate('500ms ease-in')),
		]),
	],
})
export class TicketWorkDaysMessageComponent extends TicketEditBaseComponent implements OnInit {
	protected positionContainer$: Observable<any>;
	protected controlMessage$: BehaviorSubject<string> = new BehaviorSubject(WORKING_DAYS_DURATION_FIXED);
	private timer$: Observable<number>;
	protected noteVisible$: Observable<boolean>;
	private isVisible$: BehaviorSubject<any> = new BehaviorSubject(true);
	private timerReset$: Subject<number> = new Subject();
	protected _hasTicketsInEdit$: Observable<boolean>;

	constructor(
		@Self() @Optional() public override control: NgControl,
		@Optional() public override multiWrapper: MultiWrapperComponent,
		public planState: PlanState,
		private ticketEditService: TicketEditService,
		public override cd: ChangeDetectorRef
	) {
		super(control, multiWrapper, cd);
	}

	public override ngOnInit() {
		super.ngOnInit();

		// Timer until note goes boom
		this.timer$ = this.timerReset$.pipe(
			switchMap(() => timer(5000, 5000)),
			takeUntil(this.shutdownSubject)
		);

		this.noteVisible$ = combineLatest([this.timerReset$, this.isVisible$]).pipe(
			startWith([0, false]),
			map((combined) => {
				return combined[1];
			})
		);

		this.timer$.subscribe(() => {
			this.isVisible$.next(false);
		});

		this._hasTicketsInEdit$ = this.ticketEditService.combinedEdit$.pipe(
			map((etc: EditTicketsCombined) => {
				return (
					etc &&
					etc.data.liveWorkingDays &&
					etc.data.side !== Side.PULL &&
					etc.selectedType === TicketType.Task
				);
			}),
			takeUntil(this.shutdownSubject)
		);

		this.positionContainer$ = this.ticketEditService.combinedEdit$.pipe(
			filter((etc) => !!etc),
			map((etc: EditTicketsCombined) => {
				const offset = -48; //etc.data.height;
				return {
					x: etc.data.width + etc.data.left + 32,
					y: etc.data.top + offset,
				};
			}),
			takeUntil(this.shutdownSubject)
		);
	}
}
